import React, { useState } from 'react';
import { Button, TextInput, Select } from '@etherisc/ui-kit';
import { ClientSelect } from './ClientSelect';
import { CostCenterSelect } from './CostCenterSelect';
import { useRecordPremiumEarning } from '../../hooks/insurance/useInsurancePremium';
import type { PremiumEarningRequest, InsuranceJournal } from '../../types/Insurance';

interface EarningFormData {
  clientId: string;
  costCenterId: string;
  amount: string;
  currency: string;
  earningDate: string; // ISO date string
  description?: string;
}

interface PremiumEarningFormProps {
  onSuccess?: (journal: InsuranceJournal) => void;
  onCancel: () => void;
  initialData?: Partial<EarningFormData>;
}

export const PremiumEarningForm: React.FC<PremiumEarningFormProps> = ({ 
  onSuccess, 
  onCancel,
  initialData 
}) => {
  const [formData, setFormData] = useState<EarningFormData>({
    clientId: initialData?.clientId || '',
    costCenterId: initialData?.costCenterId || '',
    amount: initialData?.amount || '',
    currency: initialData?.currency || 'EUR',
    earningDate: initialData?.earningDate || new Date().toISOString().split('T')[0],
    description: initialData?.description || ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const recordPremiumEarning = useRecordPremiumEarning();

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.clientId) {
      newErrors.clientId = 'Client is required';
    }

    if (!formData.costCenterId) {
      newErrors.costCenterId = 'Policy/Cost Center is required';
    }

    if (!formData.amount) {
      newErrors.amount = 'Earning amount is required';
    } else {
      const amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount <= 0) {
        newErrors.amount = 'Earning amount must be a positive number';
      }
    }

    if (!formData.currency) {
      newErrors.currency = 'Currency is required';
    } else if (!/^[A-Z]{3}$/.test(formData.currency)) {
      newErrors.currency = 'Currency must be a valid 3-letter code (e.g., EUR, USD)';
    }

    if (!formData.earningDate) {
      newErrors.earningDate = 'Earning date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const request: PremiumEarningRequest = {
        clientId: formData.clientId,
        costCenterId: formData.costCenterId,
        amount: formData.amount,
        currency: formData.currency,
        earningDate: formData.earningDate,
        description: formData.description || undefined
      };

      const result = await recordPremiumEarning.mutateAsync(request);
      
      if (onSuccess) {
        onSuccess(result);
      }
      
      // Reset form
      setFormData({
        clientId: '',
        costCenterId: '',
        amount: '',
        currency: 'EUR',
        earningDate: new Date().toISOString().split('T')[0],
        description: ''
      });
      
    } catch (error) {
      console.error('Failed to record premium earning:', error);
      setErrors({ submit: error instanceof Error ? error.message : 'Failed to record premium earning' });
    }
  };

  const updateFormData = (field: keyof EarningFormData) => (value: string | React.ChangeEvent<HTMLInputElement>) => {
    const actualValue = typeof value === 'string' ? value : value.target.value;
    setFormData(prev => ({ ...prev, [field]: actualValue }));
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ClientSelect 
          label="Client" 
          value={formData.clientId} 
          onChange={updateFormData('clientId')}
          required
        />
        {errors.clientId && (
          <div className="text-red-500 text-sm mt-1">{errors.clientId}</div>
        )}
        
        <CostCenterSelect 
          label="Policy/Cost Center" 
          clientId={formData.clientId}
          value={formData.costCenterId} 
          onChange={updateFormData('costCenterId')}
          required
        />
        {errors.costCenterId && (
          <div className="text-red-500 text-sm mt-1">{errors.costCenterId}</div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <TextInput 
          label="Earning Amount" 
          type="text"
          value={formData.amount} 
          onChange={updateFormData('amount')}
          placeholder="0.00"
          required
        />
        {errors.amount && (
          <div className="text-red-500 text-sm mt-1">{errors.amount}</div>
        )}
        
        <Select 
          label="Currency" 
          value={formData.currency} 
          onValueChange={updateFormData('currency')}
          required
          options={[
            { value: 'EUR', label: 'EUR' },
            { value: 'USD', label: 'USD' },
            { value: 'GBP', label: 'GBP' }
          ]}
        />
        {errors.currency && (
          <div className="text-red-500 text-sm mt-1">{errors.currency}</div>
        )}
        
        <TextInput 
          label="Earning Date" 
          type="date"
          value={formData.earningDate} 
          onChange={updateFormData('earningDate')}
          required
        />
        {errors.earningDate && (
          <div className="text-red-500 text-sm mt-1">{errors.earningDate}</div>
        )}
      </div>

      <TextInput 
        label="Description (Optional)" 
        type="text"
        value={formData.description || ''} 
        onChange={updateFormData('description')}
        placeholder="Additional notes about this premium earning..."
      />

      {errors.submit && (
        <div className="text-red-500 text-sm p-3 bg-red-50 rounded-md">
          {errors.submit}
        </div>
      )}

      <div className="flex gap-2 justify-end pt-4">
        <Button 
          variant="outline" 
          onClick={onCancel}
          disabled={recordPremiumEarning.isPending}
        >
          Cancel
        </Button>
        <Button 
          type="submit"
          disabled={recordPremiumEarning.isPending}
        >
          {recordPremiumEarning.isPending ? 'Recording...' : 'Record Premium Earning'}
        </Button>
      </div>
    </form>
  );
};
